import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './AdvancedInsights.css';
import { API_BASE_URL } from '../lib/api';

// Color schemes
const ASSET_COLORS = ['#8B5CF6', '#F59E0B', '#EC4899', '#10B981', '#3B82F6', '#EF4444', '#6366F1'];
const PERFORMANCE_COLOR = '#8B5CF6';
const INCOME_COLOR = '#27ae60';
const EXPENSE_COLOR = '#e74c3c';

function AdvancedInsights() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/analytics/dashboard`, {
        withCredentials: true
      });
      setDashboard(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value) => {
    return `${value.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <div className="insights-container">
        <div className="loading">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="insights-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="insights-container">
        <div className="empty-state">No data available yet. Add holdings to see insights.</div>
      </div>
    );
  }

  // Prepare asset allocation data for pie chart
  const assetAllocationData = Object.entries(dashboard.assetAllocation.allocation || {})
    .filter(([_, value]) => Math.abs(value) > 0)
    .map(([name, value]) => ({
      name,
      value: Math.abs(value),
      percentage: dashboard.assetAllocation.percentages[name] || 0
    }));

  // Prepare geographic diversification data
  const geographicData = Object.entries(dashboard.geographicDiversification.geographic || {})
    .filter(([_, value]) => value > 0)
    .map(([name, value]) => ({
      name,
      value,
      percentage: dashboard.geographicDiversification.percentages[name] || 0
    }));

  // Prepare net worth trend data
  const netWorthData = dashboard.netWorthTrend.map(item => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    netWorth: item.netWorth,
    totalValue: item.totalValue
  }));

  // Prepare income vs expenses data
  const cashFlowData = [
    {
      category: 'Monthly',
      income: dashboard.incomeVsExpenses.monthly.income,
      expenses: dashboard.incomeVsExpenses.monthly.expenses,
      cashFlow: dashboard.incomeVsExpenses.monthly.cashFlow
    },
    {
      category: 'Yearly',
      income: dashboard.incomeVsExpenses.yearly.income,
      expenses: dashboard.incomeVsExpenses.yearly.expenses,
      cashFlow: dashboard.incomeVsExpenses.yearly.cashFlow
    }
  ];

  return (
    <div className="insights-container">
      <div className="insights-header">
        <h2>📊 Advanced Insights</h2>
        <p className="insights-subtitle">
          Comprehensive analytics and performance metrics for your portfolio
        </p>
      </div>

      {/* Key Performance Indicators */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-label">Total Portfolio Value</div>
          <div className="kpi-value">{formatCurrency(dashboard.portfolioPerformance.totalCurrentValue)}</div>
          <div className="kpi-subtitle">Current value</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Total Return</div>
          <div className={`kpi-value ${dashboard.portfolioPerformance.totalReturn >= 0 ? 'positive' : 'negative'}`}>
            {formatPercent(dashboard.portfolioPerformance.totalReturn)}
          </div>
          <div className="kpi-subtitle">
            {formatCurrency(dashboard.portfolioPerformance.totalGainLoss)} gain/loss
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Monthly Cash Flow</div>
          <div className={`kpi-value ${dashboard.incomeVsExpenses.monthly.cashFlow >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(dashboard.incomeVsExpenses.monthly.cashFlow)}
          </div>
          <div className="kpi-subtitle">
            {formatPercent(dashboard.incomeVsExpenses.monthly.savingsRate)} savings rate
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">30-Day Performance</div>
          <div className={`kpi-value ${dashboard.portfolioPerformance.performance30Days >= 0 ? 'positive' : 'negative'}`}>
            {formatPercent(dashboard.portfolioPerformance.performance30Days)}
          </div>
          <div className="kpi-subtitle">Last month</div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-grid">
        {/* Asset Allocation Pie Chart */}
        <div className="chart-card">
          <h3>🎯 Asset Allocation</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={assetAllocationData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name, percentage }) => `${name}: ${percentage.toFixed(1)}%`}
                labelLine={false}
              >
                {assetAllocationData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={ASSET_COLORS[index % ASSET_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="chart-legend">
            {assetAllocationData.map((item, index) => (
              <div key={item.name} className="legend-item">
                <span
                  className="legend-color"
                  style={{ backgroundColor: ASSET_COLORS[index % ASSET_COLORS.length] }}
                />
                <span className="legend-label">{item.name}</span>
                <span className="legend-value">{formatCurrency(item.value)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Geographic Diversification */}
        {geographicData.length > 0 && (
          <div className="chart-card">
            <h3>🌍 Geographic Diversification</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={geographicData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(value) => formatCurrency(value)} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="value" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Net Worth Trend */}
        {netWorthData.length > 0 && (
          <div className="chart-card full-width">
            <h3>📈 Net Worth Trend (90 Days)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={netWorthData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis tickFormatter={(value) => formatCurrency(value)} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="netWorth"
                  stroke={PERFORMANCE_COLOR}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  name="Net Worth"
                />
                <Line
                  type="monotone"
                  dataKey="totalValue"
                  stroke="#10B981"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  name="Total Assets"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Income vs Expenses */}
        <div className="chart-card full-width">
          <h3>💸 Income vs Expenses</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={cashFlowData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="category" />
              <YAxis tickFormatter={(value) => formatCurrency(value)} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Bar dataKey="income" fill={INCOME_COLOR} name="Income" />
              <Bar dataKey="expenses" fill={EXPENSE_COLOR} name="Expenses" />
            </BarChart>
          </ResponsiveContainer>
          <div className="cash-flow-summary">
            <div className="summary-item">
              <span className="summary-label">Monthly Savings Rate:</span>
              <span className={`summary-value ${dashboard.incomeVsExpenses.monthly.savingsRate >= 0 ? 'positive' : 'negative'}`}>
                {formatPercent(dashboard.incomeVsExpenses.monthly.savingsRate)}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Yearly Savings Rate:</span>
              <span className={`summary-value ${dashboard.incomeVsExpenses.yearly.savingsRate >= 0 ? 'positive' : 'negative'}`}>
                {formatPercent(dashboard.incomeVsExpenses.yearly.savingsRate)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Insights */}
      <div className="insights-footer">
        <p className="insights-note">
          📅 Last updated: {new Date(dashboard.generatedAt).toLocaleString()}
        </p>
        <button className="btn-refresh" onClick={fetchDashboard}>
          🔄 Refresh Data
        </button>
      </div>
    </div>
  );
}

export default AdvancedInsights;
